// YTDraftCard — Feb 2026 (Randy pt-yt-draftcard). ONE component that renders
// every YouTube AI Reply draft across all three surfaces (Chats-tab inbox,
// Workbench Console, Refinery Console). Fixing a button once here fixes it
// everywhere.
//
// Buttons: Approve · Edit (inline) · Re-draft (CapturePad + retry) · Reject
// - Edit: tapping enters INLINE edit mode, replacing the reply text with a
//   textarea. A Save/Cancel row appears below the button row until saved.
// - Re-draft: first tap opens the CapturePad drawer (voice + text + push).
//   Closing the drawer flips the button to a lighter green. Second tap
//   actually re-drafts through the ask_silo loopback so the LLM re-runs
//   against the possibly-updated silo.
// - Approve: posts to YouTube + opens the AppendQnaModal so the creator can
//   optionally append this Q&A to a silo file that already references the
//   video (Randy's "silo memory feedback loop").
import { useState, useEffect, useRef } from "react";
import { YoutubeLogo, Check, X, PencilSimple, Warning, ArrowsClockwise, MagicWand, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ytApi } from "@/lib/ytApi";
import YTRedraftCapturePad from "@/components/YTRedraftCapturePad";
import GuidanceChip from "@/components/GuidanceChip";
import YTAppendQnaModal from "@/components/YTAppendQnaModal";
import LengthStagePicker from "@/components/LengthStagePicker";
import { readCardSettings, writeCardSettings } from "@/lib/cardSettingsStore";
import VoiceOriginalPrompt, { editRatio, ASK_THRESHOLD } from "@/components/VoiceOriginalPrompt";

// Iter160p (Randy pt-yt-length-migration, Feb 2026) — the hardcoded
// LENGTH_PRESET_OPTIONS array + its Popover are retired. This card now
// stages length via the shared <LengthStagePicker> (server-registry
// backed, custom presets pick up automatically). "Use default" is the
// picker's built-in clear-stage affordance (`showClearOption`), and
// staging semantics are preserved: null = no override → integration
// falls back to answer_length_{questions|non_questions}; any other
// preset id = one-shot override for THIS Re-draft.

// Jul 30, 2026 (Randy pt89c-xxxiii) — classical live search highlight.
// Splits `text` on the keyword (case-insensitive), wraps each match in
// a highlight <mark>. Empty keyword returns the text as-is.
function highlightText(text, kw) {
  if (!text) return text;
  const needle = (kw || "").trim().toLowerCase();
  if (!needle) return text;
  const src = String(text);
  const lower = src.toLowerCase();
  const parts = [];
  let i = 0;
  let key = 0;
  while (i < src.length) {
    const idx = lower.indexOf(needle, i);
    if (idx === -1) {
      parts.push(<span key={key++}>{src.slice(i)}</span>);
      break;
    }
    if (idx > i) parts.push(<span key={key++}>{src.slice(i, idx)}</span>);
    parts.push(
      <mark key={key++} className="bg-[#FFF3A0] text-[#1C1B1A] rounded px-0.5">
        {src.slice(idx, idx + needle.length)}
      </mark>
    );
    i = idx + needle.length;
  }
  return parts;
}

export default function YTDraftCard({ draft, siloId, siloSlug, testIdPrefix, onChanged, isPosted = false, onLocalDismiss = null, highlightKeyword = "", isFocusedMatch = false }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(draft.draftReply || "");
  const [voiceAsk, setVoiceAsk] = useState(null); // edit ratio when the "100% my words" prompt is open
  const [busy, setBusy] = useState(false);
  const [capturePadOpen, setCapturePadOpen] = useState(false);
  // "plain" | "armed" — armed means CapturePad was opened (data may have
  // changed in the silo), so the Re-draft button turns light green.
  const [redraftState, setRedraftState] = useState("plain");
  const [appendModalOpen, setAppendModalOpen] = useState(false);
  const [appendPending, setAppendPending] = useState(false);
  const [currentDraft, setCurrentDraft] = useState(draft);
  // Feb 2026 (Randy pt-yt-sent-touched) — for a `sent` draft, tracks
  // whether the creator has manually edited the text OR re-drafted it
  // since it went live. Controls the primary button label:
  //   • untouched sent → "Approve post" (dim green) — user is signing
  //     off on what the AI auto-posted; API call flips status→posted
  //     WITHOUT calling YouTube (nothing changed).
  //   • touched sent → "Update reply" (bright green) — user made
  //     changes that need to be pushed to YouTube; calls comments.update
  //     via the existing approve_draft endpoint (which already handles
  //     the update path when status is 'sent').
  // Length dropdown staging alone does NOT flip this flag — a length
  // pick is a modifier for the NEXT Re-draft, not an edit in itself.
  const [sentTouched, setSentTouched] = useState(false);

  // Jul 30, 2026 (Randy pt89c-xxxiii) — scroll-into-view when this card
  // is the currently-focused search match (Prev/Next arrows on the
  // classical search bar navigate through matches by cursor).
  const cardRef = useRef(null);
  useEffect(() => {
    if (!isFocusedMatch || !cardRef.current) return;
    try { cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); }
    catch { /* SSR / test harness — silent */ }
  }, [isFocusedMatch, currentDraft.id]);

  // Live = already on YouTube, whether it got there by full-auto correction
  // (`sent`) or by the creator approving it (`posted`, incl. the History view).
  // A function, not a const, because the edit/re-draft handlers are defined
  // above the render-scope consts that describe the same thing.
  const isLiveNow = () => isPosted || currentDraft.status === "sent" || currentDraft.status === "posted";

  const startEdit = () => { setEditText(currentDraft.draftReply || ""); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setEditText(currentDraft.draftReply || ""); };
  const saveEdit = async () => {
    const next = editText.trim();
    if (!next) return toast.error("Reply can't be empty.");
    setBusy(true);
    const wasBefore = currentDraft.draftReply || "";
    try {
      await ytApi.editDraft(currentDraft.id, next);
      setCurrentDraft((d) => ({ ...d, draftReply: next }));
      // Feb 2026 (Randy pt-yt-sent-touched) — manual edit on a sent
      // draft flips the primary button from "Approve post" → "Update
      // reply" so the creator can push the edit to YouTube.
      // Jun 2026 (Randy) — POSTED counts too: "I hit the edit button make a
      // change... however, the repost option does not occur. The button stays
      // the green lit approved." The two-mode UI below always handled a
      // touched POSTED row; nothing ever set the flag for one.
      if (isLiveNow()) setSentTouched(true);
      toast.success("Draft saved.");
      setEditing(false);
      onChanged?.();
      // Sep 29, 2026 (Randy pt-voice-kit) — offer to flag a substantially
      // rewritten reply as "100% my words" for the Voice Kit's specimen pool.
      const ratio = editRatio(wasBefore, next);
      if (ratio >= ASK_THRESHOLD) {
        setVoiceAsk(ratio);
        // Randy's case exactly: the reply is already approved and live, he
        // rewrote a chunk of it by hand, and the only prompt he saw was the
        // voice one. Offer the file too, right after he answers it.
        if (isLiveNow()) setAppendPending(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const decideVoiceOriginal = async (flag) => {
    setVoiceAsk(null);
    // Jun 2026 (Randy) — the append offer waits its turn instead of being
    // buried. *"I lost the opportunity to add this custom response to the
    // bottom of the silo file... that popup never showed up. thats a bug,
    // especially since I added a completely custom response to the commenter.
    // which means the likelihood of me WANTING it to be added goes way up."*
    // Two prompts fired from the same save-then-approve and the voice one
    // covered the other; now they queue.
    if (appendPending) { setAppendPending(false); setAppendModalOpen(true); }
    if (flag === null) return;
    try {
      await ytApi.editDraft(currentDraft.id, (currentDraft.draftReply || "").trim(), !!flag);
      if (flag) toast.success("Flagged — the Voice Kit can now pull this reply as a writing sample.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't flag that reply.");
    }
  };

  const openRedraftPad = () => setCapturePadOpen(true);
  // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — guidance from
  // "Guide this reply" as `{ text, persist } | null`. When persist
  // is true, guidance survives across successive re-drafts (until
  // user clears or edits from the chip). Chip renders next to the
  // Re-draft button so the user sees armed state.
  const [padGuidance, setPadGuidance] = useState(null);
  const closeRedraftPad = (didChangeSilo, guidance, persist) => {
    setCapturePadOpen(false);
    // Randy Q3 — arm regardless of whether user pushed. Even a closed-empty
    // pad may follow an earlier separate silo update; the user should be
    // able to retry the LLM on demand.
    setRedraftState("armed");
    if (guidance && guidance.trim()) {
      setPadGuidance({ text: guidance.trim(), persist: !!persist });
    } else if (didChangeSilo) {
      toast.success("Silo updated — tap Re-draft again to regenerate the reply.");
    }
  };
  const doRedraftNow = async () => {
    setBusy(true);
    try {
      // Feb 2026 (Randy pt-yt-length-stage) — Re-draft uses the staged
      // length override when set; otherwise falls back to the
      // integration's default Q/NQ length. Staging is one-shot: cleared
      // after each successful re-draft so the next one uses defaults.
      //
      // Iter160p — pendingLengthOverride is now a bare preset id string
      // (or null). Backward-compatible with the backend which accepts
      // both server preset ids AND the legacy short/medium/long keys.
      const opts = (pendingLengthOverride !== null && pendingLengthOverride !== undefined)
        ? { lengthPreset: pendingLengthOverride }
        : {};
      // Feb 27 2026 (Randy pt-ytre-guide-this-reply revised) — pass
      // guidance TEXT. Clear the state ONLY when NOT sticky (persist
      // false). Sticky guidance stays armed until the user edits or
      // clears via the chip.
      if (padGuidance?.text) opts.moldingHint = padGuidance.text;
      const r = await ytApi.redraft(currentDraft.id, opts);
      setCurrentDraft((d) => ({ ...d, draftReply: r.draft_reply, llmCost: (d.llmCost || 0) + (r.llm_cost_usd || 0), confidence: r.confidence ?? d.confidence }));
      setRedraftState("plain");
      // Feb 2026 (Randy pt-yt-sent-touched) — re-drafting a `sent` draft
      // flips the primary button from "Approve post" → "Update reply";
      // Jun 2026 — and an already-approved one does the same.
      if (isLiveNow()) setSentTouched(true);
      // Feb 27 2026 (Randy pt-ytre-card-settings-persist) — DO NOT
      // clear pendingLengthOverride here anymore. Randy explicitly
      // asked for these settings to be remembered per-card so the
      // user doesn't have to re-select on the next re-draft. The
      // localStorage effect keeps refresh-survival in sync.
      if (padGuidance && !padGuidance.persist) setPadGuidance(null);
      toast.success("Re-drafted.");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-draft failed.");
    } finally { setBusy(false); }
  };

  // Feb 2026 (Randy pt-yt-length-stage) — the Length ▾ pill is now
  // STAGING-ONLY. Selecting a length just remembers the pick and updates
  // the button label; it does NOT fire a re-draft. The next click on
  // Re-draft uses the staged length as a one-shot override. This makes
  // Re-draft the single "action" button and Length a pure modifier,
  // matching Randy's mental model.
  //
  // Iter160p — state is now just a preset id string (or null). The
  // shared <LengthStagePicker> owns its own popover state; its `onStage`
  // handler drives this state directly, and `showClearOption` provides
  // the "Use default" clear affordance.
  //
  // Feb 27 2026 (Randy pt-ytre-card-settings-persist) — hydrate from
  // localStorage keyed by draft.id so the length choice survives
  // refresh. Randy: "settings chosen when creating a re-draft ...
  // remembered for the specific card."
  const _draftSaved = readCardSettings(currentDraft.id);
  const [pendingLengthOverride, setPendingLengthOverride] = useState(_draftSaved?.length_id || null);
  useEffect(() => {
    writeCardSettings(currentDraft.id, { length_id: pendingLengthOverride || null });
  }, [currentDraft.id, pendingLengthOverride]);

  const doApprove = async () => {
    setBusy(true);
    try {
      const _wasUpdate = showUpdateLabel;
      await ytApi.approveDraft(currentDraft.id);
      // The row is live and clean again, so the button settles back to the
      // ✓ Approved badge until the next edit.
      setCurrentDraft((d) => ({ ...d, status: "posted" }));
      setSentTouched(false);
      toast.success(_wasUpdate ? "Reply updated on YouTube." : "Reply posted to YouTube.");
      // Open the "Save Q&A to silo?" CTA — unless the voice prompt is still up
      // from a hand-edit, in which case it opens the moment that one is answered.
      if (voiceAsk !== null) setAppendPending(true);
      else setAppendModalOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed.");
    } finally { setBusy(false); }
  };
  const doReject = async () => {
    setBusy(true);
    try { await ytApi.rejectDraft(currentDraft.id); toast("Rejected."); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed."); }
    finally { setBusy(false); }
  };
  // Oct 2026 (Randy) — "has this been answered?" He answers plenty of comments
  // the classic way inside YouTube, and with two environments on one channel a
  // reply posted from the other one is invisible here. This asks YouTube about
  // the thread, pages past the five-reply sample, and swaps HIS actual reply
  // into the card in place of the AI text. Nothing else on the card changes,
  // so the footer buttons act on the real reply: sending overwrites it.
  // Oct 23, 2026 (Randy) — *"I never did get any clear indication in the UI to
  // confirm that a question has been answered. Or not answered… I just felt
  // like nothing happened."* A toast was the only reply, and a toast is gone
  // before you look up. The button now holds the verdict itself: it reads
  // "No answer found" or "Answer found" and stays that way for this card.
  const [answerCheck, setAnswerCheck] = useState("idle");
  useEffect(() => { setAnswerCheck("idle"); }, [currentDraft.id]);
  const doCheckAnswered = async () => {
    setBusy(true);
    setAnswerCheck("checking");
    try {
      const res = await ytApi.checkAnswered(currentDraft.id);
      if (res.answered) {
        setAnswerCheck("found");
        toast.success("You already answered this on YouTube — your reply is now in the card.");
      } else if (res.reason === "thread_unavailable") {
        setAnswerCheck("idle");
        toast("YouTube didn't return that thread — it may be deleted or held for review.");
      } else {
        setAnswerCheck("none");
        toast("No reply from you on this thread yet — the draft is untouched.");
      }
      onChanged?.();
    } catch (e) {
      setAnswerCheck("idle");
      toast.error(e.response?.data?.detail || "Check failed.");
    } finally { setBusy(false); }
  };

  const initials = (currentDraft.author || "??").replace("@", "").slice(0, 2).toUpperCase();
  // Jul 30, 2026 (Randy pt89c-xxxi) — treat both `sent` (full-auto-correction)
  // AND `posted` (already-live-on-YouTube from History view) as "update
  // reply" affordance. Reject button hides when posted (backend won't
  // accept it, and the row is already live).
  const isSent = currentDraft.status === "sent";
  const isLive = isPosted || currentDraft.status === "posted";
  // Feb 2026 (Randy pt-yt-sent-touched, pt-yt-posted-touched) — SENT and
  // POSTED drafts share the same two-mode primary UI:
  //   • untouched sent  → "Approve post" (dim green button) — closes
  //     correction window, marks posted, no YT API call.
  //   • untouched posted → "✓ Approved" (non-clickable status BADGE,
  //     dim green) — nothing to do; row is already terminal.
  //   • touched sent OR posted → "Update reply" (bright green button)
  //     — pushes edit to YouTube via comments.update, marks posted.
  // Touched = manual text edit OR Re-draft. Length staging does NOT
  // count as touched (matches Randy's spec).
  const showUpdateLabel = ((isSent || isLive) && sentTouched);
  // Randy, Jun 2026: "upon saving it, the approved changes to… Repost." A
  // touched `sent` row keeps "Update reply" (his earlier wording for the
  // auto-correction case); an already-APPROVED row says Re-post.
  const isRepost = showUpdateLabel && isLive && !isSent;
  const showDimApprovePost = isSent && !sentTouched;
  const showApprovedBadge = isLive && !sentTouched;

  return (
    <>
      <div
        ref={cardRef}
        className={`border rounded-lg bg-white p-3 space-y-2 transition ${isFocusedMatch ? "border-[#F0A800] ring-2 ring-[#FFE28C]/60" : "hairline"}`}
        data-testid={`${testIdPrefix}-${currentDraft.id}`}
      >
        {/* Video + author header — Jul 30, 2026 (Randy pt89c-xxxiii)
            Shows human-readable VIDEO TITLE when available (fallback to
            youtu.be/{id} for pre-backfill rows) + a Q (green) / NQ (blue)
            tag at the end so Randy can eyeball the classification.
            Feb 2026 (Randy pt-yt-title-date-mobile) — the videoTimeAgo
            (e.g. "3d ago") used to live INSIDE the truncated span, so a
            long title ate the date on mobile. Now the date is a sibling
            element with shrink-0 so it always shows; the title truncates
            independently.
            Feb 2026 (Randy pt-yt-reply-to-reply) — depth-2 drafts also
            get a ↳ Reply to reply badge in the header + the tag switches
            to Q2/NQ2 (amber tint) so Randy can spot follow-ups at a
            glance in the inbox. */}
        <div className="text-[10.5px] text-[#6B6A68] flex items-center gap-1.5">
          <YoutubeLogo size={11} weight="fill" className="text-[#B54848] shrink-0" />
          {currentDraft.depth === 2 && (
            <span
              data-testid={`${testIdPrefix}-depth2-badge-${currentDraft.id}`}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#F5EBD9] text-[#6B5A3E] uppercase tracking-[0.14em] shrink-0"
              title="This is a REPLY-TO-REPLY — the fan responded to your AI's previous reply on this thread."
            >↳ Reply to reply</span>
          )}
          {currentDraft.answeredElsewhere && (
            <span
              data-testid={`${testIdPrefix}-answered-elsewhere-badge-${currentDraft.id}`}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#EAF3EC] text-[#3A6B47] uppercase tracking-[0.14em] shrink-0"
              title="This is your own reply, already live on YouTube. Editing and sending overwrites it; rejecting just clears this card."
            >Your reply · live</span>
          )}
          {currentDraft.kind === "limit_notice" && (
            <span
              data-testid={`${testIdPrefix}-limit-notice-badge-${currentDraft.id}`}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#FDECEC] text-[#B54848] uppercase tracking-[0.14em] shrink-0"
              title="This person has had their full allowance of answers on this thread. This is the canned hand-off pointing them at your silo — it cost nothing to produce. Re-draft it to give them one more real answer instead."
            >Final reply · limit</span>
          )}
          <span className="truncate min-w-0 flex-1">
            on <em className="not-italic text-[#3A3A38]">
              &quot;{highlightText(currentDraft.videoTitle && currentDraft.videoTitle.trim() ? currentDraft.videoTitle : (currentDraft.videoId ? `youtu.be/${currentDraft.videoId}` : "video"), highlightKeyword)}&quot;
            </em>
          </span>
          {currentDraft.videoTimeAgo && (
            <span
              data-testid={`${testIdPrefix}-video-date-${currentDraft.id}`}
              className="shrink-0 text-[10.5px] text-[#6B6A68] whitespace-nowrap"
            >
              · {currentDraft.videoTimeAgo}
            </span>
          )}
          {currentDraft.isQuestion === true && (
            <span
              data-testid={`${testIdPrefix}-tag-q-${currentDraft.id}`}
              className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-[0.14em] ml-auto shrink-0 ${currentDraft.depth === 2 ? "bg-[#F5EBD9] text-[#6B5A3E]" : "bg-[#E6F2E6] text-[#2F6B3A]"}`}
              title={currentDraft.depth === 2
                ? "Detected as a QUESTION on a follow-up reply — uses the Answer length · Questions preset (Q2)"
                : "Detected as a QUESTION — uses the Answer length · Questions preset"}
            >{currentDraft.depth === 2 ? "Q2" : "Q"}</span>
          )}
          {currentDraft.isQuestion === false && (
            <span
              data-testid={`${testIdPrefix}-tag-nq-${currentDraft.id}`}
              className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-[0.14em] ml-auto shrink-0 ${currentDraft.depth === 2 ? "bg-[#F5EBD9] text-[#6B5A3E]" : "bg-[#E6F1FF] text-[#1E40AF]"}`}
              title={currentDraft.depth === 2
                ? "Detected as a NON-QUESTION on a follow-up reply — uses the Answer length · Non-Questions preset (NQ2)"
                : "Detected as a NON-QUESTION (comment / thanks / praise) — uses the Answer length · Non-Questions preset"}
            >{currentDraft.depth === 2 ? "NQ2" : "NQ"}</span>
          )}
        </div>
        {/* Feb 2026 (Randy pt-yt-reply-to-reply) — quoted parent (our AI)
            reply text when this is a depth-2 draft. Gives Randy the
            back-and-forth context at a glance without expanding the
            thread on YouTube. Muted amber left-border matches the ↳
            badge tint. */}
        {currentDraft.depth === 2 && currentDraft.parentReplyText && (
          <div
            data-testid={`${testIdPrefix}-parent-reply-${currentDraft.id}`}
            className="text-[11px] text-[#6B5A3E] bg-[#FBF5EA] border-l-2 border-[#D9B87B] rounded-sm px-2 py-1.5 [overflow-wrap:anywhere]"
          >
            <div className="text-[9px] uppercase tracking-[0.16em] mb-0.5 opacity-70">Your AI&apos;s prior reply</div>
            <div className="leading-snug italic">{currentDraft.parentReplyText}</div>
          </div>
        )}
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-full bg-[#4A5D4E] text-white text-[9px] font-medium flex items-center justify-center shrink-0">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-medium text-[#1C1B1A]">{highlightText(currentDraft.author, highlightKeyword)}</div>
            <div className="text-[12px] text-[#3A3A38] mt-0.5 leading-snug [overflow-wrap:anywhere]">{highlightText(currentDraft.commentText, highlightKeyword)}</div>
          </div>
        </div>

        {/* Drafted reply (or skipped reason) — inline editable */}
        {currentDraft.draftReply ? (
          <div className={`${(isSent || isLive) ? "bg-[#EAF3EC] border-[#B9D4BE]" : "bg-[#FDF4EE]/60 border-[#EBC9C9]/40"} border rounded-md p-2.5`}>
            <div className={`text-[9.5px] uppercase tracking-[0.18em] mb-1 flex items-center gap-1.5 ${(isSent || isLive) ? "text-[#2F6B3A]" : "text-[#8A4844]"}`}>
              <YoutubeLogo size={9} weight="fill" /> {(isSent || isLive) ? (isLive && !isSent ? "Posted to YouTube" : "Sent to YouTube") : "Drafted reply"}
              {(isSent || isLive) && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#2F6B3A] text-white tracking-normal normal-case font-medium">{isLive && !isSent ? "POSTED" : "SENT"}</span>}
              <span className="ml-auto text-[9px] text-[#6B6A68] tracking-normal normal-case">${(currentDraft.llmCost || 0).toFixed(3)} · {Math.round((currentDraft.confidence || 0) * 100)}% confident</span>
            </div>
            {editing ? (
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={Math.min(10, Math.max(3, editText.split("\n").length + 1))}
                data-testid={`${testIdPrefix}-edit-textarea-${currentDraft.id}`}
                className="w-full bg-white border border-[#8A4844]/30 rounded px-2 py-1.5 text-[12px] text-[#3A3A38] leading-snug focus:outline-none focus:border-[#8A4844]"
                autoFocus
              />
            ) : (
              <div
                className="text-[12px] text-[#3A3A38] leading-snug whitespace-pre-wrap [overflow-wrap:anywhere]"
                data-testid={`${testIdPrefix}-body-${currentDraft.id}`}
              >{highlightText(currentDraft.draftReply, highlightKeyword)}</div>
            )}
          </div>
        ) : (
          <div className="bg-[#F0F1EE] rounded-md p-2 text-[11px] text-[#6B6A68] italic flex items-center gap-1.5">
            <Warning size={11} weight="duotone" /> {currentDraft.skippedReason}
          </div>
        )}

        {/* Primary button row: Approve · Edit · Re-draft · Reject */}
        {currentDraft.draftReply && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            {/* Feb 2026 (Randy pt-yt-posted-touched) — POSTED + untouched
                shows a non-clickable ✓ Approved badge instead of a button,
                because there's no action to take (row is already terminal
                on YouTube). The moment the user edits or Re-drafts, we
                swap to the bright Update reply button. */}
            {showApprovedBadge ? (
              <span
                data-testid={`${testIdPrefix}-approved-badge-${currentDraft.id}`}
                className="text-[11px] px-3 py-1.5 rounded-md bg-[#F4F8F5] border border-[#B9D4BE] text-[#2F6B3A] inline-flex items-center gap-1 cursor-default select-none"
                title="This reply is live on YouTube and approved. Tap Re-draft or Edit to make changes."
              >
                <Check size={11} weight="bold" /> Approved
              </span>
            ) : (
              /* Feb 2026 (Randy pt-yt-sent-touched) — dim green for
                 untouched sent (soft "Approve post" action); bright
                 green for touched sent (real "Update reply" action)
                 or any pending draft. */
              <button onClick={doApprove} disabled={busy || editing}
                data-testid={`${testIdPrefix}-${isRepost ? "repost" : "approve"}-${currentDraft.id}`}
                className={`text-[11px] px-3 py-1.5 rounded-md text-white flex items-center gap-1 disabled:opacity-50 ${
                  showDimApprovePost
                    ? "bg-[#8CBF95] hover:bg-[#7AAD84]"
                    : isRepost
                      ? "bg-[#C16E5A] hover:bg-[#A85A47]"
                      : "bg-[#2F6B3A] hover:bg-[#245029]"
                }`}
                title={showDimApprovePost
                  ? "Approve post — closes the correction window early and moves this out of the inbox to Posted. YouTube reply text stays as-is (no update call)."
                  : isRepost
                    ? "Re-post — pushes your edited text to the live YouTube reply, keeping the same comment."
                    : showUpdateLabel
                      ? "Update reply — pushes your edited text to YouTube via update_reply, then moves this to Posted."
                      : "Approve — posts to YouTube and moves this to Posted."}
              >
                <Check size={11} weight="bold" />
                {showDimApprovePost
                  ? "Approve post"
                  : (isRepost ? (busy ? "Re-posting…" : "Re-post") : (showUpdateLabel ? "Update reply" : "Approve"))}
              </button>
            )}
            <button onClick={startEdit} disabled={busy || editing}
              data-testid={`${testIdPrefix}-edit-${currentDraft.id}`}
              className="text-[11px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-50 flex items-center gap-1">
              <PencilSimple size={11} weight="bold" /> Edit
            </button>
            <button
              onClick={redraftState === "armed" ? doRedraftNow : openRedraftPad}
              disabled={busy || editing}
              data-testid={`${testIdPrefix}-redraft-${currentDraft.id}`}
              className={`text-[11px] px-3 py-1.5 rounded-md flex items-center gap-1 disabled:opacity-50 ${
                redraftState === "armed"
                  ? "bg-[#2F6B3A] text-white hover:bg-[#245029]"
                  : "border hairline text-[#4A5D4E] hover:bg-[#F0F1EE]"
              }`}
              title={redraftState === "armed"
                ? "Silo data was updated in the CapturePad — click to regenerate this reply through the ask_silo pipeline."
                : "Open the CapturePad to update silo memory, then re-draft this reply."}
            >
              {redraftState === "armed" ? <MagicWand size={11} weight="bold" /> : <ArrowsClockwise size={11} weight="bold" />}
              {redraftState === "armed" ? "Re-draft now" : "Re-draft"}
            </button>
            {/* Feb 27 2026 (Randy pt-ytre-guide-chip) — visible chip
                so Randy can see his guidance is armed. Click the
                body to reopen the pad in edit mode; X to clear. */}
            <GuidanceChip
              guidance={padGuidance}
              onEdit={() => setCapturePadOpen(true)}
              onClear={() => setPadGuidance(null)}
              testId={`${testIdPrefix}-${currentDraft.id}`}
            />
            {/* Feb 2026 (Randy pt-yt-length-stage) — Length ▾ is a
                STAGING selector, not an action. Picking a length just
                updates the button's label to show the staged pick; the
                actual re-draft only fires when the user presses
                Re-draft. Once Re-draft runs (with the staged length),
                the stage is cleared for the next round.

                Iter160p (Randy pt-yt-length-migration, Feb 2026) —
                migrated from the hardcoded 7-preset local popover to
                the shared <LengthStagePicker> (server-registry backed
                via /api/s/{slug}/answer-styles). Owner-added custom
                presets pick up automatically. `showClearOption` +
                `unstagedLabel="Length"` preserves the original YT
                Replies UX: plain "Length ▾" button when no override,
                blue-highlighted "Length: {label}" when staged. */}
            <LengthStagePicker
              slug={siloSlug}
              staged={pendingLengthOverride}
              onStage={(id) => setPendingLengthOverride(id)}
              disabled={busy || editing}
              entryId={`${testIdPrefix}-${currentDraft.id}`}
              showClearOption
              unstagedLabel="Length"
              promptLabel="Stage length for next Re-draft"
            />
            {isLive && !isSent && onLocalDismiss ? (
              /* Clear this post — LOCAL-ONLY dismiss for Posted Today / History
                 views. Hides the row from the current pill view; if the user
                 tabs away and comes back OR re-searches, the row re-appears
                 (nothing is deleted, nothing is unposted). Randy pt89c-xxxii. */
              <button onClick={() => onLocalDismiss(currentDraft.id)} disabled={busy || editing}
                data-testid={`${testIdPrefix}-clear-${currentDraft.id}`}
                className="text-[11px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-50 flex items-center gap-1">
                <X size={11} weight="bold" /> Clear this post
              </button>
            ) : (
              <button onClick={doReject} disabled={busy || editing}
                data-testid={`${testIdPrefix}-reject-${currentDraft.id}`}
                className="text-[11px] px-3 py-1.5 rounded-md text-[#B54848] hover:bg-[#FDECEC] disabled:opacity-50 flex items-center gap-1">
                <X size={11} weight="bold" /> {isSent ? "Clear from inbox" : "Reject"}
              </button>
            )}
            {/* Oct 2026 (Randy) — far right of the row, after everything else.
                Asks YouTube whether he has already answered this himself.
                Oct 23 — demoted: the inbox sweep retires most of these on load,
                so this is the instant check for the 15-minute window the sweep
                hasn't covered yet, and the only way to pull a reply he wrote by
                hand into the card so an edit UPDATES it. Smaller, and it now
                states its own result instead of relying on a toast. */}
            {!isLive && (
              <button onClick={doCheckAnswered}
                disabled={busy || editing || answerCheck === "found"}
                data-testid={`${testIdPrefix}-check-answered-${currentDraft.id}`}
                title="Ask YouTube whether you've already replied to this. If you have, your real reply replaces the draft here and sending it overwrites it rather than posting twice."
                className={`ml-auto shrink-0 text-[10px] px-2 py-1 rounded-md disabled:opacity-60 flex items-center gap-1 ${
                  answerCheck === "found"
                    ? "text-[#3A6B4A] bg-[#EAF4ED]"
                    : answerCheck === "none"
                      ? "text-[#8A8880] bg-[#F0F1EE]"
                      : "text-[#6B6A68] hover:bg-[#F0F1EE]"
                }`}>
                {answerCheck === "found" ? <Check size={10} weight="bold" />
                  : <MagnifyingGlass size={10} weight="bold" />}
                {answerCheck === "checking" ? "Checking…"
                  : answerCheck === "found" ? "Answer found"
                    : answerCheck === "none" ? "No answer found"
                      : "Already answered?"}
              </button>
            )}
          </div>
        )}

        {/* Edit-mode Save/Cancel — Randy Q2b: temp dropdown row just below
            the main button row, hidden otherwise. */}
        {editing && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid={`${testIdPrefix}-edit-dropdown-${currentDraft.id}`}>
            <button onClick={saveEdit} disabled={busy}
              data-testid={`${testIdPrefix}-edit-save-${currentDraft.id}`}
              className="text-[11px] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1">
              <Check size={11} weight="bold" /> Save
            </button>
            <button onClick={cancelEdit} disabled={busy}
              data-testid={`${testIdPrefix}-edit-cancel-${currentDraft.id}`}
              className="text-[11px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-50 flex items-center gap-1">
              <X size={11} weight="bold" /> Cancel
            </button>
            <span className="text-[10.5px] text-[#6B6A68] italic ml-1">Edit any text above, then Save.</span>
          </div>
        )}
      </div>

      {/* Slim CapturePad drawer for the Re-draft flow */}
      {capturePadOpen && (
        <YTRedraftCapturePad
          siloId={siloId}
          initialText={padGuidance?.text || ""}
          initialPersist={!!padGuidance?.persist}
          onClose={(didChangeSilo, guidance, persist) => closeRedraftPad(didChangeSilo, guidance, persist)}
        />
      )}

      {/* Append-Q&A CTA after Approve */}
      {appendModalOpen && (
        <YTAppendQnaModal
          draftId={currentDraft.id}
          onClose={() => { setAppendModalOpen(false); onChanged?.(); }}
        />
      )}

      {/* Sep 29, 2026 (Randy pt-voice-kit) — "flag as 100% original response
          for voice cadence training", only after a substantial hand-edit. */}
      <VoiceOriginalPrompt
        open={voiceAsk !== null}
        ratio={voiceAsk || 0}
        onDecide={decideVoiceOriginal}
        text={editText || currentDraft.draftReply || ""}
        siloId={siloId}
        defaultTitle={`My reply — ${currentDraft.videoTitle || "YouTube comment"}`}
      />
    </>
  );
}
